/**
 * Pure reading of the server's AMBIGUOUS_IDENTIFIER refusal into tappable
 * choices, and the write-back of a tapped choice into the command's fields.
 *
 * The backend never picks between two matching pucks or two identically
 * labelled boxes — guessing there moves the wrong physical sample — so it
 * refuses with 409 and hands back what it matched
 * (`inventory-backend/services/inventoryCommands.js:365-380` for the box,
 * `:430-437` for the puck). The old refusal asked the user to "say which one",
 * which for boxes was unanswerable: they are ambiguous BECAUSE they share a
 * label, so repeating the label cannot separate them. What differs is WHERE
 * they are, which is why the box refusal carries `locations` and why a tap is
 * the natural answer.
 *
 * That structured object arrives as `err.serverDetail`. It is deliberately a
 * different field from `err.detail`, which carries the human copy sentence:
 * one is rendered to the user as prose, the other is read field by field, and
 * a reader who confuses them gets a string where an object belongs.
 *
 * Two shapes come back in `serverDetail` and they are not interchangeable:
 *
 *   puck  { identifier, candidates: ['VP-1', 'VP_1'] }
 *         each candidate IS an answer — the serial goes straight back into the
 *         field that was ambiguous.
 *   box   { boxId, candidates: [name…], locations: ['VP1 well 1', 'RML-4 well 3'] }
 *         the answer is not the box, it is the PUCK the box sits in, because
 *         `puckIdentifier` is the only narrowing the API accepts
 *         (`resolveBox`, `inventoryCommands.js:303-312`). When both boxes are
 *         in the same puck that narrowing cannot separate them either, so the
 *         refusal is honestly unresolvable by tapping and `resolvable` is
 *         false: show the sentence, offer no chips, and let the screen answer.
 *
 * No browser globals, no I/O — `scripts/test-voice-candidates.js` runs this
 * module directly under node.
 */

const AMBIGUOUS_CODE = "AMBIGUOUS_IDENTIFIER";

/**
 * The backend's own identifier normalisation
 * (`inventoryCommands.js:80`). Copied rather than approximated: deciding WHICH
 * field was ambiguous by comparing the spoken text to what the server echoed
 * only works if both sides collapse "VP-1", "vp 1" and "VP1" the same way.
 */
const speechKey = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/**
 * The weaker, HONEST comparison: the identifier as it was actually written,
 * differing only in surrounding space and in case.
 *
 * `speechKey` deliberately collapses punctuation, which is right for deciding
 * that a spoken "VP - 1" and an echoed "VP1" are the same name. It is wrong for
 * deciding WHICH of two fields the server was talking about, because a lab
 * holding "VP-1" and "VP_1" collapses both to one key: with a source and a
 * destination both spoken "VP1", every tap matched the destination, the source
 * the server was actually refusing never moved, and the next refusal echoed
 * "VP1" again. The user tapped a new destination each time and the command
 * never resolved.
 */
const exactKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const WELL_SEPARATOR = " well ";

/**
 * 'VP1 well 4' -> 'VP1'. The last separator wins so a puck label that itself
 * contains the word does not lose its tail.
 *
 * A location with no separator at all is the backend's fallback for a box
 * whose parent puck did not populate (`well 3`, `inventoryCommands.js:360`).
 * That yields "" on purpose: there is no puck name to send, and an empty key
 * collapses those locations together so `resolvable` correctly comes out
 * false instead of offering chips that cannot resolve anything.
 */
function puckOf(location) {
  const text = String(location ?? "");
  const cut = text.lastIndexOf(WELL_SEPARATOR);
  return cut === -1 ? "" : text.slice(0, cut);
}

/** Ambiguity needs at least two answers; one "choice" is not a question. */
const MIN_CHOICES = 2;

function puckCandidates(serverDetail) {
  // A puck with no serialNo maps to undefined upstream. Dropping those keeps
  // the count in the question equal to the number of chips actually shown, and
  // keeps "undefined" from being sent back as a puck identifier.
  const serials = serverDetail.candidates
    .map((serial) => (serial == null ? "" : String(serial)))
    .filter(Boolean);
  if (serials.length < MIN_CHOICES) return null;

  const identifier =
    serverDetail.identifier == null ? "" : String(serverDetail.identifier);
  return {
    kind: "puck",
    identifier,
    question: `${serials.length} pucks match "${identifier}"`,
    choices: serials.map((serial) => ({
      value: serial,
      label: serial,
      sublabel: null,
    })),
  };
}

function boxCandidates(serverDetail) {
  const locations = serverDetail.locations.map((loc) => String(loc ?? ""));
  if (locations.length < MIN_CHOICES) return null;

  // `candidates` is filtered by the server (`.filter(Boolean)`) so it can be
  // shorter than `locations`; it is a sublabel, never the index of truth.
  const names = Array.isArray(serverDetail.candidates)
    ? serverDetail.candidates
    : [];
  const identifier = serverDetail.boxId == null ? "" : String(serverDetail.boxId);
  const pucks = locations.map(puckOf);

  return {
    kind: "box",
    identifier,
    question: `${locations.length} boxes are called ${identifier}`,
    choices: locations.map((loc, i) => ({
      value: pucks[i],
      label: loc,
      sublabel: names[i] || null,
    })),
    // More than one distinct puck is exactly the condition under which saying
    // (or tapping) a puck narrows the match. Same puck twice: it does not.
    resolvable: new Set(pucks).size > 1,
  };
}

/**
 * @param {Error|null} err an error thrown by `voiceFetch`, carrying `code` and
 *   `serverDetail` as packet P1.0 attaches them. NOT `err.detail`, which on the
 *   same Error is the human copy sentence — a string, and never these fields.
 * @returns {object|null} the render model, or null when this refusal is not an
 *   answerable ambiguity.
 */
export function candidatesFromRefusal(err) {
  if (!err || err.code !== AMBIGUOUS_CODE) return null;

  const serverDetail = err.serverDetail;
  if (
    !serverDetail ||
    typeof serverDetail !== "object" ||
    Array.isArray(serverDetail)
  ) {
    return null;
  }

  // Box first. A box refusal carries `candidates` too, so `locations` is the
  // only field that tells the two shapes apart.
  if (Array.isArray(serverDetail.locations)) return boxCandidates(serverDetail);
  if (Array.isArray(serverDetail.candidates)) return puckCandidates(serverDetail);
  return null;
}

/**
 * Which field a puck choice belongs in when the echoed identifier matches
 * neither of the ones we sent. Deletes carry no destination at all
 * (`commandController.js:36`), so the source qualifier is the only field there.
 */
function defaultPuckField(intent) {
  const name = String(intent ?? "").replace(/^preview_/, "");
  return name.startsWith("delete") ? "puckIdentifier" : "destinationPuckIdentifier";
}

/**
 * @param {string} intent 'move_box' | 'create_box' | 'delete_box'
 * @param {object} fields the fields the refused preview was sent with
 * @param {object} choice one `choices` entry plus the parent's `identifier`
 * @param {string} kind 'puck' | 'box'
 * @returns {object} a NEW fields object; `fields` is never mutated, because the
 *   caller still holds it as the state of the card being re-previewed.
 */
export function fieldsWithChoice(intent, fields, choice, kind) {
  const base = fields && typeof fields === "object" ? fields : {};
  if (!choice) return { ...base };

  const value = choice.value == null ? "" : String(choice.value);

  // The box itself stays as spoken. What the tap adds is the puck it lives in,
  // which is the only thing that narrows a duplicated box label.
  if (kind === "box") return { ...base, puckIdentifier: value };

  let field = null;

  // Pass one: the identifier EXACTLY as the server echoed it — and the server
  // echoes back the spelling WE sent (`resolvePuck`'s 409 carries
  // `{ identifier, candidates }` with the caller's own string). Source first,
  // because the source is resolved first: `previewMoveBox` hands
  // `puckIdentifier` to `resolveBox` before it resolves
  // `destinationPuckIdentifier` (`inventoryCommands.js:1013-1042`), so a
  // refusal that could be about either field is about the source. That order is
  // what makes two taps terminate: the first writes the source, the second
  // refusal echoes the still-ambiguous spelling, and by then only the
  // destination still carries it.
  const echoed = exactKey(choice.identifier);
  if (echoed) {
    if (exactKey(base.puckIdentifier) === echoed) {
      field = "puckIdentifier";
    } else if (exactKey(base.destinationPuckIdentifier) === echoed) {
      field = "destinationPuckIdentifier";
    }
  }

  // Pass two, unchanged: the identifier was transcribed with different
  // punctuation from the one the server echoed ("VP - 1" against "VP1"), so
  // neither field matches literally. Destination first here, because it is the
  // field a spoken puck name normally lands in and on a move the source
  // qualifier is usually absent. An empty key must never match an absent
  // field, which is why both passes are guarded.
  const wanted = field ? "" : speechKey(choice.identifier);
  if (wanted) {
    if (speechKey(base.destinationPuckIdentifier) === wanted) {
      field = "destinationPuckIdentifier";
    } else if (speechKey(base.puckIdentifier) === wanted) {
      field = "puckIdentifier";
    }
  }

  return { ...base, [field || defaultPuckField(intent)]: value };
}
